import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Bell, Mail } from 'lucide-react';
import type { NotificationInternalOptIn } from '../../lib/api';
import type { SubscriptionChannel } from './useSubscriptionTree';

interface InternalOptInRowProps {
  /** The domain this row sits under, e.g. "purchasing". */
  domainKey: string;
  domainLabel: string;
  optIn: NotificationInternalOptIn;
  /** False when the tenant's master email switch is off. */
  emailsEnabled: boolean;
  saving: boolean;
  onChange: (domain: string, channel: SubscriptionChannel, enabled: boolean) => void;
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The group-internal opt-in under one notification section (CR-7255 review).
 *
 * Mail to the selling side of a relationship inside the person's own group is
 * off by default (nobody there owns it). The section's own switches go on
 * governing every other customer; these say whether to include the group's own
 * customers too — per channel, so turning on the bell never turns on email.
 */
export function InternalOptInRow({
  domainKey, domainLabel, optIn, emailsEnabled, saving, onChange,
}: InternalOptInRowProps) {
  const state = optIn.domains[domainKey];
  const isOn = (channel: SubscriptionChannel) =>
    Boolean(optIn.all_domains[channel] || state?.[channel]);
  const what = domainKey === 'purchasing' ? 'Orders' : `${domainLabel} notifications`;
  const who = joinNames(optIn.customers);
  const verb = optIn.customers.length === 1 ? 'it is' : 'they are';

  return (
    <div className="ml-4 rounded-md border border-dashed p-3 text-sm" data-testid={`internal-opt-in-${domainKey}`}>
      <p className="text-muted-foreground">
        {`${what} from ${who} are off by default — ${verb} part of your own group.`}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-6">
        <span className="font-medium">Include them:</span>
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm">In-app</Label>
          <Switch
            checked={isOn('bell')}
            disabled={saving}
            onCheckedChange={next => onChange(domainKey, 'bell', next)}
            aria-label={`Include ${who} in in-app notifications for ${domainLabel}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm">Email</Label>
          <Switch
            checked={emailsEnabled && isOn('email')}
            disabled={saving || !emailsEnabled}
            onCheckedChange={next => onChange(domainKey, 'email', next)}
            aria-label={`Include ${who} in email notifications for ${domainLabel}`}
          />
        </div>
      </div>
    </div>
  );
}
